import { FC, useCallback, useMemo, useState } from "react";
import { SiteBody } from "@vviinn/user-managment-client-ts";

import { useNotifications } from "../../app/useNotifications";
import { responseNotification } from "../../components/Notifications";
import { AcceptableNamedRow } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/AcceptableNamedRow";
import { CollapsedFields } from "../../components/GeneralUI/CollapsedFields/CollapsedFields";
import { AttributeGroupWrapper } from "../../ui/layout/detailedPages/AttributeGroupWrapper";
import { TypedBooleanPromise } from "../../helpers/functions/types";
import { useAppSelector } from "../../store";
import { compareAsStrings } from "../../helpers";
import { AppearedContent } from "../../components/DataDisplay/AppearedContent";

type Props = {
  groupItems: boolean;
  refinementColumns: string[];
  updateSiteField: TypedBooleanPromise<SiteBody>;
};

export const ItemGrouping: FC<Props> = ({
  groupItems,
  refinementColumns,
  updateSiteField,
}) => {
  const { notify } = useNotifications();
  const [localGroupItems, setLocalGroupItems] = useState(groupItems);
  const [localColumns, setLocalColumns] = useState(refinementColumns);

  const defaultFilters = useAppSelector((state) => state.campaigns.filters);
  const { customFieldsTitles, loading } = useAppSelector((state) => state.site);

  const initConfig = useCallback(() => {
    setLocalGroupItems(groupItems);
    setLocalColumns(refinementColumns);
  }, [groupItems, refinementColumns]);

  const relevantColumns = useMemo(
    () => [...defaultFilters, ...customFieldsTitles].sort(compareAsStrings),
    [defaultFilters, customFieldsTitles],
  );

  const hasChanges = useMemo(() => {
    if (localGroupItems !== groupItems) return true;
    if (!localGroupItems) return false;
    return (
      [...localColumns].sort().join("\n") !==
      [...refinementColumns].sort().join("\n")
    );
  }, [localGroupItems, groupItems, localColumns, refinementColumns]);

  const acceptChange = useCallback(async () => {
    notify("Info", "Updating item grouping");
    const ok = await updateSiteField({
      productUniquenessLevel: localGroupItems
        ? "item_group_id"
        : "product_code",
      refinementColumns: localColumns,
    });
    return responseNotification(notify, ok, "Item grouping");
  }, [localGroupItems, localColumns, notify, updateSiteField]);

  return (
    <AcceptableNamedRow
      title="Item grouping"
      permissionsGroup="site:advanced_config"
      initConfig={initConfig}
      allowSubmit={hasChanges}
      onSubmit={acceptChange}
      content={groupItems ? (refinementColumns?.length ?? 0) : "Inactive"}
      buttonTitle="Save settings"
    >
      <CollapsedFields
        active={localGroupItems}
        title="Group Items"
        activateFields={() => setLocalGroupItems(true)}
        resetFields={() => setLocalGroupItems(false)}
        permissions="site:advanced_config:update"
      >
        <AppearedContent isLoaded={!loading} skeletonOptions={{ amount: 1 }}>
          <div className="grid gap-6 p-4">
            <AttributeGroupWrapper
              initAttributesGroup={localColumns}
              handleAttributesGroup={setLocalColumns}
              groupAttributesList={relevantColumns}
              attributeGroupTitle="Split Groups by"
            />
          </div>
        </AppearedContent>
      </CollapsedFields>
    </AcceptableNamedRow>
  );
};
